import axios, { AxiosInstance } from 'axios'
import { ANALYSIS_LOG_CATEGORY, ANALYSIS_LOG_PLATFORM, AnalysisLogAction } from './constants'

export type ActionLogParams = Record<string, unknown>

export type ApiConfig = {
  apiBasePath: string
  sessionToken?: string
  extraLogParams: Record<string, unknown>
}

/**
 * Removes null or undefined values from an object.
 * @param obj - The object to compact.
 * @returns The compacted object.
 */
function compactObject<T extends Record<string, any>>(obj: T) {
  return Object.fromEntries(Object.entries(obj).filter(([, value]) => value != null)) as T
}

/**
 * Converts the keys of an object from camel case to snake case.
 *
 * @param obj - The object whose keys need to be converted.
 * @returns The object with keys converted to snake case.
 */
function decamelizeKeys<T extends Record<string, any>>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key.replace(/([A-Z])/g, '_$1').toLowerCase(),
      value,
    ]),
  ) as T
}

class ApiClient {
  api: AxiosInstance
  extraLogParams: Record<string, unknown>

  constructor({ apiBasePath, sessionToken, extraLogParams }: ApiConfig) {
    this.api = axios.create({
      baseURL: apiBasePath,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Set session token if provided
    if (sessionToken) {
      this.api.defaults.headers['X-Mitene-Session'] = sessionToken
    }

    this.extraLogParams = extraLogParams
  }

  setSessionToken(sessionToken: string | undefined): void {
    if (sessionToken) {
      this.api.defaults.headers['X-Mitene-Session'] = sessionToken
    } else {
      delete this.api.defaults.headers['X-Mitene-Session']
    }
  }

  logAnalysisAction = async (action: AnalysisLogAction, params?: ActionLogParams) => {
    const basePayload = {
      platform: ANALYSIS_LOG_PLATFORM,
      category: ANALYSIS_LOG_CATEGORY,
      action,
      ...this.extraLogParams,
    }

    const payload = params ? decamelizeKeys(compactObject(params)) : {}

    try {
      await this.api.post('/v1/analysis/json', {
        ...basePayload,
        ...payload,
      })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err)
    }
  }
}

export default ApiClient
